import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Markdown } from '../../components/Markdown';
import { SectionTitle, Spinner } from '../../components/ui';
import { packageById } from '../../../shared/packages';
import type { EngagementBundle } from '../EngagementDetail';

const TIMELINE_LABELS: Record<string, string> = {
  asap: 'ASAP', this_quarter: 'This quarter', this_year: 'This year', exploring: 'Exploring',
};
const BUDGET_LABELS: Record<string, string> = {
  under_5k: '<$5k', '5k_15k': '$5k–15k', '15k_50k': '$15k–50k', '50k_plus': '$50k+', unsure: 'TBD',
};

export function OverviewTab({ bundle, reload }: { bundle: EngagementBundle; reload: () => Promise<void> }) {
  const e = bundle.engagement;
  const pkg = packageById(e.recommended_offer);
  const brief = bundle.artifacts.find((a) => a.type === 'pre_call_brief');
  const [briefContent, setBriefContent] = useState<string | null>(null);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [generating, setGenerating] = useState(false);

  const signals: string[] = (() => {
    try { return e.offer_signals ? (JSON.parse(e.offer_signals) as string[]) : []; }
    catch { return []; }
  })();

  useEffect(() => {
    if (!brief) { setBriefContent(null); return; }
    setLoadingBrief(true);
    api.get<{ artifact: { content_md: string } }>(`/api/artifacts/${brief.id}`)
      .then((r) => setBriefContent(r.artifact.content_md))
      .catch(() => setBriefContent(null))
      .finally(() => setLoadingBrief(false));
  }, [brief?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function generateBrief() {
    setGenerating(true);
    try {
      await api.post(`/api/engagements/${e.id}/brief`, {});
      await reload();
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-1">
        <div className="card">
          <SectionTitle>Submission facts</SectionTitle>
          <dl className="space-y-2 text-sm">
            {[
              ['Goal', e.primary_goal],
              ['Self-rated maturity', e.ai_maturity_self_rating != null ? `${e.ai_maturity_self_rating}/5` : null],
              ['Current tools', e.current_ai_tools],
              ['Regulatory', e.regulatory_exposure],
              ['Timeline', e.timeline ? TIMELINE_LABELS[e.timeline] ?? e.timeline : null],
              ['Budget', e.budget_range ? BUDGET_LABELS[e.budget_range] ?? e.budget_range : null],
              ['Compelling event', e.compelling_event],
              ['Domain', e.company_domain],
            ].map(([label, value]) =>
              value ? (
                <div key={label as string}>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                  <dd className="text-slate-200">{value}</dd>
                </div>
              ) : null,
            )}
          </dl>
        </div>

        <div className="card">
          <SectionTitle>Recommended offer</SectionTitle>
          {pkg ? (
            <>
              <div className="font-bold text-brand-300">{pkg.name}</div>
              <div className="text-xs text-slate-500">{pkg.duration}</div>
              <p className="mt-1 text-sm text-slate-400">{pkg.tagline}</p>
            </>
          ) : (
            <p className="text-sm text-slate-500">No recommendation yet.</p>
          )}
          {signals.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-slate-400">
              {signals.map((s) => <li key={s} className="flex gap-1.5"><span className="text-brand-400">→</span>{s}</li>)}
            </ul>
          )}
        </div>

        <div className="card">
          <SectionTitle>Workflow progress</SectionTitle>
          <ul className="space-y-1.5 text-sm text-slate-300">
            <li>📋 Assessment: {bundle.counts.assessedDimensions}/10 dimensions{bundle.counts.assessmentAvg != null ? ` · avg ${bundle.counts.assessmentAvg}/5` : ''}</li>
            <li>💡 Use cases: {bundle.counts.useCases}</li>
            <li>🛡️ NIST mappings: {bundle.counts.nistMappings}</li>
            <li>⚠️ Open risks: {bundle.counts.openRisks}</li>
            <li>📄 Artifacts: {bundle.artifacts.length}</li>
          </ul>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="card">
          <SectionTitle
            right={
              <button onClick={() => void generateBrief()} disabled={generating} className="btn-secondary !px-3 !py-1.5 text-xs">
                {generating ? 'Generating…' : brief ? '↻ Regenerate brief' : '✨ Generate pre-call brief'}
              </button>
            }
          >
            Pre-call brief
          </SectionTitle>
          {loadingBrief ? (
            <Spinner label="Loading brief…" />
          ) : briefContent ? (
            <div className="max-h-[70vh] overflow-y-auto pr-2">
              <Markdown content={briefContent} />
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-slate-500">
              No brief yet — intake generates one automatically; or click Generate.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
